import React, { useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  HelpCircle,
  X,
  Lightbulb,
  BookOpen,
} from "lucide-react";

const CONFIG_GUIDES = {
  MERCHANT_GENERAL_CONFIGURATIONS: {
    title: "General Settings",
    steps: [
      "Toggle 'Show pickup address on label' to control label display",
      "Toggle 'Fetch drop customer details by phone' for auto-fill",
      "Enable order confirmation flags for manual and channel orders",
      "Save your changes",
    ],
    tips: [
      "Merchant ID is auto-assigned and cannot be changed",
      "Order confirmation flags work together with Order Confirmation config",
    ],
  },
  MERCHANT_SHIPMENT_LABEL_CONFIGURATIONS: {
    title: "Shipment Label",
    steps: [
      "View the default label template assigned to your account",
      "Check carrier-specific label overrides if configured",
      "Contact support to change label template or add carrier overrides",
    ],
    tips: [
      "Label templates are managed by admin and locked per carrier",
      "A4 and thermal (4×6) formats are supported",
    ],
  },
  MERCHANT_SHIPMENT_INVOICE_CONFIGURATIONS: {
    title: "Shipment Invoice",
    steps: [
      "View the default invoice template assigned to your account",
      "Check carrier-specific invoice overrides if configured",
      "Contact support to change invoice template or add overrides",
    ],
    tips: [
      "Invoice templates are managed by admin",
      "Invoice PDFs are auto-attached to shipment emails if enabled",
    ],
  },
  MERCHANT_ORDER_SUMMARY_REPORT_CONFIGURATIONS: {
    title: "Order Summary Report",
    steps: [
      "Select which columns to include in your exported reports",
      "Choose the default date range for report generation",
      "Save your column preferences",
    ],
    tips: [
      "Reports can be downloaded from the Shipments page",
      "Column selection affects all exported reports",
    ],
  },
  MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS: {
    title: "Scheduled Email Reports",
    steps: [
      "Click 'Add Schedule' to create a new automated report",
      "Select report type, frequency, and run time (IST)",
      "Add recipient emails (To, CC, BCC) and configure the date range",
      "Optionally filter by order statuses, then save",
    ],
    tips: [
      "Run times use 24-hour IST format (e.g. 09:00, 14:30)",
      "You can add multiple schedules for different report types",
      "Column selection is managed in Order Summary Report config",
    ],
  },
  MERCHANT_COD_REMITTANCE_REPORT_CONFIGURATIONS: {
    title: "COD Remittance",
    steps: [
      "View your current remittance cycle and reversal window",
      "Contact support to update remittance settings",
    ],
    tips: [
      "This configuration is managed by admin",
      "Remittance cycle changes do not affect in-progress settlements",
    ],
  },
  MERCHANT_EXTERNAL_SHIPMENT_NOTES_CONFIGURATIONS: {
    title: "Amazon Shipment Notes",
    steps: [
      "View the provider and dimension settings for your account",
      "Toggle flags for notes visibility and automation",
      "Save your changes",
    ],
    tips: [
      "Primarily used for Amazon/FBA shipments",
      "Accurate dimensions help avoid carrier surcharges",
    ],
  },
  MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS: {
    title: "SMS Notifications",
    steps: [
      "Toggle SMS notifications on/off",
      "Set sender name (max 6 characters)",
      "Configure status-to-text mappings for each shipment event",
      "Save your configuration",
    ],
    tips: [
      "Pricing is set by admin and shown as read-only",
      "DND-registered numbers may not receive promotional SMS",
      "Use testing mode to verify before going live",
    ],
  },
  MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS: {
    title: "WhatsApp Notifications",
    steps: [
      "Toggle WhatsApp notifications on/off",
      "Configure status-to-template mappings for each event",
      "Use testing mode to verify before going live",
      "Save your configuration",
    ],
    tips: [
      "Pricing is set by admin and shown as read-only",
      "WhatsApp has higher delivery rates than SMS",
      "Customers must have WhatsApp installed to receive messages",
    ],
  },
  MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS: {
    title: "Order Confirmation",
    steps: [
      "Select confirmation criteria (COD, Prepaid, or both)",
      "Set delay, max attempts, and retry interval",
      "Toggle IVR fallback if WhatsApp fails",
      "Save - this also enables confirmation flags in General Settings",
    ],
    tips: [
      "Confirmation reduces RTO (Return to Origin) significantly",
      "Pricing is set by admin and shown as read-only",
      "Max 3 retry attempts are recommended",
    ],
  },
  MERCHANT_NDR_CUSTOMER_INTERACTION_CONFIGURATIONS: {
    title: "NDR Interaction",
    steps: [
      "Toggle NDR customer interaction on/off",
      "Enable manual call configuration if needed",
      "View channel priority (managed by admin)",
      "Save your changes",
    ],
    tips: [
      "NDR interaction can recover up to 30% of failed deliveries",
      "Channel priority and pricing are managed by admin",
      "WhatsApp + IVR combination gives best reach",
    ],
  },
  MERCHANT_WEBHOOK_CONFIGURATIONS: {
    title: "Webhook",
    steps: [
      "Enter your webhook endpoint URL",
      "Configure authentication headers or tokens",
      "Review which events trigger webhook calls",
      "Save and verify your endpoint receives events",
    ],
    tips: [
      "Webhook endpoint must respond with 2xx within 10 seconds",
      "Failed webhooks are retried up to 3 times with exponential backoff",
      "Use the integration guide PDF for detailed setup instructions",
    ],
  },
};

export default function ConfigDetailWrapper({
  category,
  onBack,
  children,
}) {
  const [showGuide, setShowGuide] = useState(false);
  const guide = CONFIG_GUIDES[category.key];

  return (
    <div className="animate-in slide-in-from-right-2 duration-300">
      {/* Breadcrumb + Help */}
      <div className="flex items-center justify-between gap-2 sm:gap-3 mb-4 sm:mb-5">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 overflow-hidden">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors shrink-0 touch-manipulation min-h-[44px] sm:min-h-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300 shrink-0 hidden xs:block" />
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
              {React.createElement(category.icon, { className: "w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600" })}
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 truncate">{category.label}</h2>
          </div>
        </div>

        {guide && (
          <button
            type="button"
            onClick={() => setShowGuide(!showGuide)}
            className={`inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors shrink-0 touch-manipulation min-h-[44px] sm:min-h-0 ${
              showGuide
                ? "bg-blue-50 text-blue-700 border border-blue-200"
                : "text-slate-500 hover:text-blue-600 hover:bg-blue-50 border border-transparent"
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            <span className="hidden sm:inline">{showGuide ? "Hide Guide" : "Setup Guide"}</span>
          </button>
        )}
      </div>

      {/* Inline Guide Panel */}
      {showGuide && guide && (
        <div className="mb-4 sm:mb-5 rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/80 to-indigo-50/50 p-3 sm:p-4 md:p-5 animate-in slide-in-from-top-1 duration-200">
          <div className="flex items-start justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 shrink-0" />
              <h3 className="text-xs sm:text-sm font-semibold text-blue-900">{guide.title}</h3>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="p-1.5 text-blue-400 hover:text-blue-700 rounded transition-colors touch-manipulation"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Steps */}
          <div className="space-y-2 mb-3 sm:mb-4">
            <p className="text-[10px] sm:text-xs font-semibold text-blue-700 uppercase tracking-wider">Steps</p>
            <ol className="space-y-1.5">
              {guide.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2 sm:gap-2.5 text-xs sm:text-sm text-slate-700">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-600 text-white text-[10px] sm:text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Tips */}
          {guide.tips && guide.tips.length > 0 && (
            <div className="pt-3 border-t border-blue-100">
              <div className="flex items-center gap-1.5 mb-2">
                <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Tips</p>
              </div>
              <ul className="space-y-1">
                {guide.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Config content */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="p-3 sm:p-4 md:p-5 lg:p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
